import React from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';

interface ConfirmDeleteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => Promise<void>;
    isDeleting: boolean;
    user: {
        firstName: string;
        lastName: string;
        email: string;
    } | null;
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    isDeleting,
    user
}) => {
    const handleConfirm = async () => {
        await onConfirm();
    };

    if (!isOpen || !user) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-red-100 rounded-lg">
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Deactivate User</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        disabled={isDeleting}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-gray-700 mb-4">
                        Are you sure you want to deactivate <strong>{user.firstName} {user.lastName}</strong>?
                    </p>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                        <p className="text-sm text-yellow-800">
                            <strong>What happens when you deactivate a user:</strong>
                        </p>
                        <ul className="mt-2 text-sm text-yellow-800 list-disc list-inside space-y-1">
                            <li>The user will no longer be able to log in</li>
                            <li>All their data and audit history will be preserved</li>
                            <li>You can reactivate them later if needed</li>
                            <li>Email: <span className="font-mono">{user.email}</span></li>
                        </ul>
                    </div>

                    <p className="text-sm text-gray-600">
                        This action does not permanently delete the user account. It can be reversed by reactivating the user.
                    </p>
                </div>

                {/* Footer */}
                <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isDeleting}
                        className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isDeleting}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
                    >
                        {isDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {isDeleting ? 'Deactivating...' : 'Deactivate User'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmDeleteModal;